import styled from '@emotion/styled'

import Button from '@segment/matcha/components/Button'

import { trackButtonClick } from '@segment/tracking/scripts'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import type { PageConfig } from '@segment/contexts/page'

type ListItemProps = {
  text: string
  tooltip?: string
}

type BenefitProps = {
  title: string
  list: ListItemProps[]
}

type PricingCards = {
  title: string
  excerpt: string[]
  button: JSX.Element
  benefitsHeading: string
  benefits: BenefitProps[]
}

const getCards = (config: PageConfig): PricingCards[] => [
  {
    title: `Connections`,
    excerpt: [
      `Collect real-time event data from 450+ pre-built sources, including the warehouse, and send unified customer data to your favorite marketing and analytics tools.`,
      `Plans: Free, Team, and Business`,
    ],
    button: (
      <StyledButton
        color="primary"
        size="large"
        variant="outlined"
        href={getAbsoluteUrl(`/pricing/connections/`, config)}
        onClick={() =>
          trackButtonClick({
            buttonText: `Learn about Connections plans`,
            targetUrl: `/pricing/connections/`,
            buttonId: `pricing-landing-page-connections-plan`,
          })
        }
      >
        Learn about Connections plans
      </StyledButton>
    ),
    benefitsHeading: `What you can do with Connections:`,
    benefits: [
      {
        title: `Enable engineering teams to:`,
        list: [
          { text: `Collect and unify real-time events with customer data in the warehouse` },
          { text: `Send customer data to 450+ destinations` },
          { text: `Govern data for quality and compliance` },
        ],
      },
      {
        title: `Optional add-on`,
        list: [{ text: `Advanced data governance`, tooltip: `Automatically enforce data quality at scale` }],
      },
    ],
  },
  {
    title: `Customer Data Platform (CDP)`,
    excerpt: [
      `The most complete, extensible, and scalable CDP that enables technical and business users to create unified profiles, build audiences, orchestrate personalized journeys in real-time, and enhance every experience with data from your warehouse.`,
      `Plans: Unify, Unify Plus, and Engage`,
    ],
    button: (
      <StyledButton
        color="primary"
        variant="filled"
        size="large"
        href={getAbsoluteUrl(`/pricing/customer-data-platform/`, config)}
        onClick={() =>
          trackButtonClick({
            buttonText: `Learn about CDP plans`,
            targetUrl: `/pricing/customer-data-platform/`,
            buttonId: `pricing-landing-page-cdp-plan`,
          })
        }
      >
        Learn about CDP plans
      </StyledButton>
    ),
    benefitsHeading: `Everything in Connections AND:`,
    benefits: [
      {
        title: `Enable engineering teams to:`,
        list: [
          { text: `Automatically generate identity-resolved profiles` },
          { text: `Collect and unify real-time events with customer data in the warehouse` },
          { text: `Enrich and activate unified profile data to downstream tools` },
          { text: `Govern data for quality and compliance` },
        ],
      },
      {
        title: `Enable marketing teams to:`,
        list: [
          { text: `Get a complete view of the customer` },
          { text: `Create and manage audiences` },
          { text: `Orchestrate customer journeys and campaigns` },
          { text: `Leverage predictive and generative AI` },
        ],
      },
      {
        title: `Optional add-on`,
        list: [
          { text: `Advanced data governance`, tooltip: `Automatically enforce data quality at scale` },
          {
            text: `Audience & profile enrichment `,
            tooltip: `Expand identity-resolved profiles to include all warehouse data`,
          },
          {
            text: `AI recommended audiences`,
            tooltip: `Build more targeted audiences based on AI-powered product recommendations designed to drive conversions`,
          },
        ],
      },
    ],
  },
]

const StyledButton = styled(Button)`
  width: 100%;
  margin-bottom: 20px;
`

export default getCards
